/**
 * Grand Quack Auto — duck evolution system.
 * 7 chaos tiers with GLTF models from Poly Pizza (CC-BY/CC0).
 * Mesh swaps with scale-bounce + shake + fanfare on level-up.
 *
 * Tier data + GLB preloader live in src/data/duckTiers.ts.
 * Cinematic DOM/audio helpers live in src/ui/evoCinematic.ts.
 * Both splits exist so this file stays under the project's 200-line cap.
 */

import * as CANNON from 'cannon-es';
import * as THREE from 'three';
import { DUCK } from '../config';
import {
  DUCK_TIERS, type DuckTier, getLoadedDuckModel, preloadDuckModels,
} from '../data/duckTiers';
import { attachHatWithTracking, type HatType } from '../entities/hats';
import type { System, World } from '../types';
import { isDeathActive } from '../ui/deathScreen';
import {
  playEvoQuack, playLevelUpSound, showEvoFlash, showEvoText, updateNameDisplay,
} from '../ui/evoCinematic';
import { getCops } from './copSystem';
import { triggerShake } from './juiceSystem';

// Re-export so existing import sites (e.g. statsScreen.ts) keep working.
export { DUCK_TIERS } from '../data/duckTiers';
export type { DuckTier } from '../data/duckTiers';

// ── Module state ──────────────────────────────────────────────────────────

let currentTierIndex = 0;
let levelUpAt = 0;
let evolving = false; // lock to prevent overlapping evolutions
let lastEvolvedAt = -Infinity; // game-time when the last evolution cinematic ended
let proceduralChildren: THREE.Object3D[] = [];
let preloadKicked = false;

export function isEvolving(): boolean { return evolving; }
export function getLastEvolvedAt(): number { return lastEvolvedAt; }

/** Map wanted-level tier (0-based DUCK_TIERS index) to hat. Tier 0 = no hat. */
export function hatForTier(tierIndex: number): HatType | null {
  if (tierIndex <= 0) return null;        // Lil' Quacker — no hat
  if (tierIndex === 1) return 'cone';     // ★ Nuisance
  if (tierIndex === 2) return 'party';    // ★★ Menace
  return 'crown';                          // ★★★+ Chaos Duck and beyond
}

function replaceDuckMesh(world: World, tier: DuckTier): void {
  const duck = world.duck;
  if (proceduralChildren.length === 0) proceduralChildren = [...duck.children];
  while (duck.children.length > 0) duck.remove(duck.children[0]!);

  if (!tier.modelPath) {
    for (const child of proceduralChildren) duck.add(child);
  } else {
    const model = getLoadedDuckModel(tier.modelPath);
    if (model) {
      const clone = model.clone();
      const box = new THREE.Box3().setFromObject(clone);
      const size = box.getSize(new THREE.Vector3());
      const s = 0.8 / Math.max(size.y, 0.01);
      clone.scale.setScalar(s);
      const center = box.getCenter(new THREE.Vector3());
      clone.position.set(-center.x * s, -box.min.y * s, -center.z * s);
      duck.add(clone);
    } else {
      for (const child of proceduralChildren) duck.add(child);
    }
  }

  // Apply tier scale — duck grows dramatically as it evolves.
  // Lil' Quacker (0.6x) → Duck Norris (1.8x) = 3x size difference!
  duck.scale.setScalar(tier.scale);

  // Resize the collision sphere to match the new body size. Use the
  // documented cannon-es removeShape/addShape pattern instead of mutating
  // the private shapes/shapeOffsets/shapeOrientations arrays directly.
  const duckBody = world.physics?.duckBody;
  if (duckBody && duckBody.shapes.length > 0) {
    const newRadius = DUCK.bodyRadius * tier.scale;
    duckBody.removeShape(duckBody.shapes[0]!);
    duckBody.addShape(new CANNON.Sphere(newRadius));
    duckBody.updateBoundingRadius();
    duckBody.updateMassProperties();
    // Lift the body so the (now larger) sphere isn't buried in the ground.
    duckBody.position.y = Math.max(duckBody.position.y, newRadius);
  }
}

/** The Duck-volution cinematic sequence — 4.5s total. */
function swapDuckModel(world: World, tierIndex: number): void {
  if (evolving) return; // prevent overlapping evolutions
  evolving = true;

  const tier = DUCK_TIERS[tierIndex]!;
  const prevTier = tierIndex > 0 ? DUCK_TIERS[tierIndex - 1]! : null;

  // Clean up any stale overlays from previous evolution.
  document.getElementById('evo-flash')?.remove();
  document.getElementById('evo-text')?.remove();

  // Phase 1: FREEZE (0ms).
  world.time.timeScale = 0;
  triggerShake(0.6);

  // Phase 2: WHITE FLASH + swap mesh (300ms).
  setTimeout(() => {
    showEvoFlash();
    replaceDuckMesh(world, tier);
  }, 300);

  // Phase 3: ANNOUNCEMENT + sound + hat (500ms).
  setTimeout(() => {
    const oldName = prevTier ? prevTier.name : 'NOBODY';
    showEvoText(oldName, tier.name);
    playEvoQuack();
    playLevelUpSound();
    const hat = hatForTier(tierIndex);
    if (hat) attachHatWithTracking(world.duck, hat);
  }, 500);

  // Phase 4: RESUME (4500ms) — long enough to absorb what happened.
  setTimeout(() => {
    world.time.timeScale = 1;
    evolving = false;
    levelUpAt = performance.now() / 1000;
    lastEvolvedAt = world.time.elapsed;

    // Push nearby cops away so the player has breathing room post-evolution.
    const duck = world.duck.position;
    const MIN_DIST = 12;
    for (const cop of getCops()) {
      const dx = cop.group.position.x - duck.x;
      const dz = cop.group.position.z - duck.z;
      const dist = Math.hypot(dx, dz);
      if (dist < MIN_DIST && dist > 0.01) {
        const scale = MIN_DIST / dist;
        cop.group.position.x = duck.x + dx * scale;
        cop.group.position.z = duck.z + dz * scale;
      }
    }
  }, 4500);

  updateNameDisplay(tier, tierIndex);
}

export function getCurrentTierName(): string {
  return DUCK_TIERS[currentTierIndex]?.name ?? '';
}

export function getCurrentTierIndex(): number {
  return currentTierIndex;
}

export const duckEvolutionSystem: System = {
  name: 'duckEvolution',

  fixedUpdate(_dt: number, world: World) {
    if (!preloadKicked) { preloadKicked = true; preloadDuckModels(); }

    // Don't evolve during ANY end-game state.
    if (evolving || isDeathActive()
      || document.getElementById('stats-screen')
      || document.getElementById('death-screen')
      || document.getElementById('absolute-quackery')) return;

    // Check if chaos has crossed a new tier threshold.
    const chaos = world.chaos.points;
    let targetTier = 0;
    for (let i = DUCK_TIERS.length - 1; i >= 0; i--) {
      if (chaos >= DUCK_TIERS[i]!.chaosThreshold) {
        targetTier = i;
        break;
      }
    }

    // Only evolve UP — never de-evolve. Once you earn a tier, you keep it.
    if (targetTier > currentTierIndex) {
      currentTierIndex = targetTier;
      swapDuckModel(world, targetTier);
    }
  },

  render(_dt: number, _alpha: number, world: World) {
    // Skip bounce during evolution cinematic.
    if (evolving) return;

    const baseScale = DUCK_TIERS[currentTierIndex]?.scale ?? 1;
    const timeSinceLevelUp = performance.now() / 1000 - levelUpAt;
    if (timeSinceLevelUp < 0.6) {
      const t = timeSinceLevelUp / 0.6;
      const bounce = baseScale * (1 + 0.3 * (1 - t) * Math.cos(t * Math.PI * 3));
      world.duck.scale.setScalar(bounce);
    } else {
      world.duck.scale.setScalar(baseScale);
    }
  },
};
